/**
 * 形象选择**弹框**(2026-08-07 R1-b 起;2026-08-08 由抽屉改回弹框)。
 *
 * ★为什么从页面里搬进抽屉:形象在整个出片流程里**只选一次**,却永久占着版面 ——
 *   测试机实测照片档 + 预置形象态,这一块自己就 1405px(1920 上 2005px),
 *   把出片必填的文案框顶到 y=1904 / y=2504(第 2.1 / 2.3 屏)。
 *   根因是 grid-cols-4 固定列数 + aspect-square:列数不随宽度长,多出来的宽度全转成高度。
 *
 * ★2026-08-08 从抽屉改回**弹框**(用户报「选人和选形象那个按钮很小」时一并改):
 *   ① **全仓一致** —— 选人/选素材/选动作在别的舱一律是 Dialog
 *      (CharacterPickerDialog / BrollMaterialPicker / MotionPickerDialog),抽屉是这里自造的偏离;
 *   ② 抽屉当初图的是「非模态、边看文案边选」,但 560 的抽屉盖住的恰恰是 **Dock**
 *      —— 形象预览、音色、语速,全是用户正在配的东西。挡住正在配的,比看不见文案更糟;
 *   ③ 弹框宽得多:20 款公共形象 4 列排得开,抽屉里只能 3 列。
 *
 * ★「按人物选」是**第四个平级 tab**,不再藏在「我的形象」里面(它曾经比选形象还深一层)。
 *   用 `CharacterPickerBody` 而不是再套一层 Dialog —— 弹框套弹框说不清「关掉的是哪一层」。
 *
 * 内容(三来源 tab / 授权闸门顺序 / 上传口文案 / 按人物选 / 各网格)是从创建页**原样搬运**的,
 * 一个字没改 —— 那些文案背后各有一次踩坑(闸门必须排在上传之前、视频档不提供预置来源等)。
 */
import { useEffect, useRef, useState } from "react";

import { ImageIcon, Loader2, Maximize2, Plus, Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Label } from "@/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  CharacterPickerBody,
  type CharacterPick,
} from "@/entities/character/ui/CharacterPickerDialog";
import { characterHasDhVideo, type CharacterDto } from "@/entities/character/api/characterService";
import { IMAGE_ACCEPT, VIDEO_ACCEPT } from "@/shared/lib/uploadImageFormats";
import type { AvatarProfileDto, PresetAvatarDto } from "../../api/digitalHumanService";
import { DhMaterialChecklist } from "./DhMaterialChecklist";

export interface DhAvatarPickerProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /**
   * 形象定下来了 → 收抽屉。
   * ★选预置/我的形象是**一步到位**,选完即收;上传路径不自动收 —— 照片档还要在这里勾肖像授权
   *   (授权是出片门控),收早了用户就没地方勾。上传路径靠底部「用这个形象」显式收。
   */
  onPicked: () => void;

  filmType: "video" | "photo" | "tts";
  avatarKind: "upload" | "preset" | "profile";
  setAvatarKind: (k: "upload" | "preset" | "profile") => void;
  allowPhoto: boolean;

  consent: boolean;
  setConsent: (v: boolean) => void;
  uploading: boolean;
  avatar: { key: string; url: string; file?: File } | null;
  pendingVideo: File | null;
  onPickAvatar: (f: File | undefined) => void;
  /** 形象上传的报错(大小/时长/重名/网络…)—— 必须显示在弹窗里,页面上的会被遮罩盖住(0928)。 */
  uploadError?: string | null;
  /** 0901:把刚上传的照片存进形象库(opt-in;授权是 enroll 硬门,没勾不给点)。 */
  onSaveUploadedToProfiles: () => void;
  savingToProfiles: boolean;

  profilesLoading: boolean;
  visibleProfiles: AvatarProfileDto[];
  avatarProfileId: string;
  setAvatarProfileId: (id: string) => void;

  presetsLoading: boolean;
  presetAvatars: PresetAvatarDto[];
  presetAvatarId: string;
  setPresetAvatarId: (id: string) => void;

  pickCharacter: (p: CharacterPick) => void;

  /** 去人物库(0916 起形象并入人物库):空态引导 + 「管理形象」都走它。 */
  onGoAvatarLibrary: () => void;

  /** 源视频大小硬上限(MB);清单里要写真数,不另抄一份。 */
  maxVideoMb: number;
  /** 1020 源视频最长时长文案(按出片通道取,页面经 useDhVideoLimits 传入);缺省老口径。 */
  maxVideoDurationLabel?: string;
}

export function DhAvatarPicker(props: DhAvatarPickerProps) {
  const {
    open,
    onOpenChange,
    onPicked,
    filmType,
    avatarKind,
    setAvatarKind,
    allowPhoto,
    consent,
    setConsent,
    uploading,
    avatar,
    pendingVideo,
    onPickAvatar,
    uploadError,
    onSaveUploadedToProfiles,
    savingToProfiles,
    profilesLoading,
    visibleProfiles,
    avatarProfileId,
    setAvatarProfileId,
    presetsLoading,
    presetAvatars,
    presetAvatarId,
    setPresetAvatarId,
    pickCharacter,
    onGoAvatarLibrary,
    maxVideoMb,
    maxVideoDurationLabel,
  } = props;

  /**
   * 2026-09-09 老板「上传真人收进我的形象做成加号」:三 tab 视图(我的形象/预置/按人物选),
   * 上传真人不再是平级 tab,而是「我的形象」里的 + 格子展开的子态(uploadOpen)。
   * ★view 与 avatarKind 解耦:此前「点 tab=setAvatarKind」,看一眼预置库就把 kind 改了;
   *   现在 kind 只在真动作时变(点 + 进上传态/点中某个形象),浏览不改选中。
   */
  const [view, setView] = useState<"mine" | "preset" | "character">(
    avatarKind === "preset" ? "preset" : "mine"
  );
  const [uploadOpen, setUploadOpen] = useState(avatarKind === "upload");
  /**
   * 开抽屉时按「有没有形象」自动落位(每次打开只裁一次,不跟用户抢方向盘):
   * 没存过形象 → 直接进上传态(与旧默认 tab=上传真人 等价);有形象且还没传过 → 落格子墙
   * (老板拍板:有我的形象时上传收进加号)。已传了文件的照旧回上传态看预览。
   */
  const autoRoutedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoRoutedRef.current = false;
      return;
    }
    if (autoRoutedRef.current || profilesLoading || view !== "mine") return;
    autoRoutedRef.current = true;
    if (avatarKind === "upload" && !avatar) {
      setUploadOpen(visibleProfiles.length === 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profilesLoading]);
  /** 看大图的那张(0812 爱民:格子是裁过的方图,光看缩略图挑不出人)。 */
  const [previewAvatar, setPreviewAvatar] = useState<PresetAvatarDto | null>(null);

  /**
   * ★上传口只收**这条线自己那种原料**(2026-08-13 用户反馈)。
   *
   * 此前两条线共用一句「推荐上传真人短视频…也可传一张正面照(走照片档)」+ 图片视频通吃的
   * `accept` —— 但 `onPickAvatar` 早就按线硬拦了:视频线传照片会被弹去照片线,反之亦然。
   * 于是文件选择框里挑得中、传了却传不上去,是**先许诺再拒绝**。选择框里就不该出现另一条线的格式。
   *
   * (accept 只是过滤不是校验,拖拽仍可绕过 → 创建页那道走错门拦截保留,是纵深防线。)
   */
  const uploadCopy =
    filmType === "video"
      ? {
          accept: VIDEO_ACCEPT,
          hint: (
            <>
              <span className="font-medium text-foreground">上传你的真人出镜短视频</span>
              (mp4/mov,带脸带声、10~15 秒最稳,最长 {maxVideoDurationLabel ?? "2 分钟"}
              ):保留原片画面只换台词,最像真人、可长口播、能用原声。
            </>
          ),
          button: "上传视频",
          aria: "上传真人出镜视频",
        }
      : {
          accept: IMAGE_ACCEPT,
          hint: (
            <>
              <span className="font-medium text-foreground">上传一张清晰的单人正面照</span>
              (png/jpeg/webp):不用拍视频,一张正脸照就能开口说话。
            </>
          ),
          button: "上传照片",
          aria: "上传形象照",
        };

  /**
   * ★「按人物选」只列**在这条线真能出片**的人物(2026-08-13 用户反馈:不支持就别展示)。
   *
   * ★0915 归一批 B 后只剩一道坎:**这个人物有没有本线要的那种介质的素材**。
   * 另两道随归一消失 —— 「没挂真人形象」(素材直接挂人物,没有挂接这回事)、
   * 「形象不是你的」(owner-only 已按批示一撤除,租户内共用)。
   *
   * 判据只看列表 DTO 自带的 `hasDhVideo`(1006:后端按 dh_source 或 identity 槽有视频算,
   * 老后端缺省回落 `hasVideoAvatar`,见 characterHasDhVideo)—— 不在这里另查一遍素材,
   * 避免两处判据字面漂移。
   */
  const characterUsable = (c: CharacterDto) =>
    filmType === "video" ? characterHasDhVideo(c) : (c.materialCount ?? 0) > 0;

  // 「这个形象能用了吗」= 与页面 avatarReady 同口径:上传路径要 consent,其余选中即可。
  const canConfirm =
    avatarKind === "upload"
      ? allowPhoto && !!avatar && consent
      : avatarKind === "profile"
        ? allowPhoto && !!avatarProfileId
        : !!presetAvatarId;

  /** ★按钮上写**缺的那一样**:照片已经传了却还说「先选一个形象」,用户会去找哪里没选。 */
  const confirmLabel = canConfirm
    ? "用这个形象"
    : avatarKind === "upload" && avatar && !consent
      ? "先勾肖像授权"
      : "先选一个形象";

  const tabCls = (active: boolean) =>
    `flex-1 rounded px-2 py-1.5 text-xs transition ${
      active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
    }`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ★className 不许带 position utility(如 relative):tailwind-merge 会拿它顶掉基类的
          `fixed`,弹窗从视口居中变成顺流排到页面底部(视口外),用户只见遮罩「卡住」。
          布局上也不需要 —— 四角装饰的 absolute 锚点由基类 Content 承担。锁:dialog.position-lock.test.ts */}
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-3 sm:max-w-3xl"
        data-testid="dh-avatar-sheet"
      >
        <DialogHeader>
          <DialogTitle className="text-base">选 AI 分身形象</DialogTitle>
          <DialogDescription className="text-xs">
            这条口播由谁来念。选好就回去写文案。
          </DialogDescription>
        </DialogHeader>

        {/* ★0915 归一批 B:「我的素材」这个平级 tab 退役 —— 归一后素材属于人物,
            "所有素材的扁平列表"不再是一个有意义的集合,它的功能整个被「按人物选」覆盖
            (选人 = 选这个人的脸)。上传升为平级 tab(原先是「我的素材」下的 + 子态)。
            ★高亮跟 view 走,不跟 avatarKind:看一眼别的 tab 不该改选中(旧双高亮账的根治)。 */}
        <div className="flex shrink-0 rounded-md border p-0.5">
          <button
            type="button"
            onClick={() => {
              setView("mine");
              setUploadOpen(true);
            }}
            className={tabCls(view === "mine")}
          >
            上传
          </button>
          {/* 预置形象库是照片资产 → 视频档没有这个来源(不是禁用,是这档本就不提供)。 */}
          {filmType === "photo" && (
            <button
              type="button"
              onClick={() => setView("preset")}
              className={tabCls(view === "preset")}
            >
              预置形象
            </button>
          )}
          <button
            type="button"
            onClick={() => setView("character")}
            className={tabCls(view === "character")}
          >
            按人物选
          </button>
        </div>

        {/* 网格可能很长(20 款预置)→ 自滚在弹框内部,不把弹框撑破 */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {view === "character" ? (
            <>
              <p className="text-xs text-muted-foreground">
                选一位人物,自动带出 TA 的素材与声纹。
                {/* ★0915 归一:去掉 realOnly —— 虚拟人(动漫角色)同样能驱动(批示二),
                    实测动漫脸也过人脸预检。也去掉 owner-only 那句:真人授权后公司共用(批示一)。 */}
                这里只列有{filmType === "video" ? "视频" : "照片"}素材的人物 ——
                其余的在这条线出不了片。
              </p>
              <CharacterPickerBody
                filter={characterUsable}
                emptyHint={`你的人物里还没有${filmType === "video" ? "视频" : "照片"}素材 —— 去人物库打开一个人物,在「分身口播素材」区传${filmType === "video" ? "一段出镜视频" : "一张正面照"}。`}
                onSelect={(pick) => {
                  pickCharacter(pick);
                  // 按人物选是一步到位(形象+声纹一起带出)→ 选完就收,回去写文案。
                  onPicked();
                }}
              />
            </>
          ) : view === "mine" && uploadOpen ? (
            !allowPhoto ? (
              <p className="text-sm text-muted-foreground">
                租户已关闭真人照片上传。可改用「预置形象」出片,或联系超管在「租户设置」开启真人照片。
              </p>
            ) : (
              <>
                {/* 上传是「我的形象」里 + 展开的子态 —— 给条回去的路,别做成死胡同 */}
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => setUploadOpen(false)}
                >
                  ← 返回我的素材
                </button>
                <p className="text-xs text-muted-foreground">{uploadCopy.hint}</p>
                {/* 授权闸门排在上传之前:视频入形象库需 consent 存证(后端硬门),
                  闸门若长在上传按钮下方,用户必然先点上传再撞错(2026-07-15 用户实测)。 */}
                {/*
                 * ★0812 爱民:「这个按钮不明显(授权勾选)」。
                 *   它原来是一行**和上面提示文字同一个字号、同一个色阶**的普通勾选 ——
                 *   而它是这条路上唯一的**红线动作**(法律授权 + 后端硬门),也是上传的前置闸。
                 *   长得像脚注就会被当脚注跳过,然后在上传那一步撞错。
                 * ★做显眼 ≠ 替他勾:默认仍不勾,闸一个字不动。没勾时整块用 warning 底
                 *   把「这儿还没过」摆在脸上,勾了转成 success 底表示这道闸过了。
                 */}
                <label
                  htmlFor="dh-consent"
                  data-testid="dh-consent-block"
                  data-consent={consent ? "on" : "off"}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm transition ${
                    consent
                      ? "border-success/50 bg-success/10"
                      : "border-warning bg-warning/15 font-medium"
                  }`}
                >
                  <Checkbox
                    id="dh-consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(v === true)}
                    aria-label="肖像授权确认"
                    className="mt-0.5"
                  />
                  <span>
                    我已获得肖像本人授权,同意用该{filmType === "video" ? "视频" : "照片"}
                    生成口播视频。
                    {!consent && (
                      <span className="mt-0.5 block text-xs font-normal opacity-90">
                        必须先勾这一项才能上传 —— 这是本人授权存证,不是免责声明。
                      </span>
                    )}
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <Label
                    htmlFor="dh-avatar-input"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Upload className="h-4 w-4" />
                    {uploading ? "上传中…" : avatar ? "重新上传" : uploadCopy.button}
                  </Label>
                  <input
                    id="dh-avatar-input"
                    data-testid="dh-avatar-input"
                    type="file"
                    accept={uploadCopy.accept}
                    aria-label={uploadCopy.aria}
                    className="hidden"
                    onChange={(e) => {
                      onPickAvatar(e.target.files?.[0]);
                      // 清空:再选同一个文件浏览器不触发 onChange = 又一个「点了没反应」(0928)
                      e.target.value = "";
                    }}
                  />
                  {avatar && (
                    <img
                      src={avatar.url}
                      alt="形象照预览"
                      className="h-16 w-16 rounded-md border object-cover"
                    />
                  )}
                  {/* 0901:照片默认一次性用,想复用/进批量要显式存进形象库。
                      视频线不出现:那条路 enrollVideo 本来就自动入库。 */}
                  {avatar?.file && filmType === "photo" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="dh-save-avatar-to-profiles"
                      disabled={!consent || savingToProfiles}
                      title={consent ? undefined : "先勾上方肖像授权,才能存进素材库"}
                      onClick={onSaveUploadedToProfiles}
                    >
                      {savingToProfiles ? "保存中…" : "加入我的素材"}
                    </Button>
                  )}
                </div>
                {avatar?.file && filmType === "photo" && (
                  <p className="text-2xs text-muted-foreground">
                    存进「我的素材」后,下次出片免重传,批量出片里也能选到这张脸。
                  </p>
                )}
                {uploadError && (
                  <p
                    role="alert"
                    data-testid="dh-upload-error"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    {uploadError}
                  </p>
                )}
                {pendingVideo && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    已选「{pendingVideo.name}」· 勾选上方授权后自动上传
                  </p>
                )}
                {/* P6 原料自检清单:类型/大小已在上传口硬拦,这里讲的是**拦不了**的那几条
                    (正脸/单人/光线/别遮脸)+ 不合规会怎样。一个字都不说「已检测通过」。 */}
                <DhMaterialChecklist
                  kind={filmType === "video" ? "video" : "photo"}
                  maxVideoMb={maxVideoMb}
                  maxVideoDurationLabel={maxVideoDurationLabel}
                />
              </>
            )
          ) : view === "mine" ? (
            !allowPhoto ? (
              <p className="text-sm text-muted-foreground">
                租户已关闭真人照片。可改用「预置形象」出片,或联系超管在「租户设置」开启真人照片。
              </p>
            ) : profilesLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : visibleProfiles.length === 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {/* 列表按线过滤过(visibleProfiles)→ 空态说的必须是**这条线要的那种**形象,
                      否则视频线上会指着用户去存一张照片,存完回来照样是空的。 */}
                  还没有保存的{filmType === "video" ? "视频" : "照片"}形象。点下面的 + 上传
                  {filmType === "video" ? "一段真人出镜视频" : "一张本人正面照"},或先去
                  <button
                    type="button"
                    className="mx-1 underline underline-offset-2 hover:text-primary"
                    onClick={onGoAvatarLibrary}
                  >
                    人物库
                  </button>
                  存好再回来复用。
                </p>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  <button
                    type="button"
                    aria-label="上传真人"
                    onClick={() => {
                      setAvatarKind("upload");
                      setUploadOpen(true);
                    }}
                    className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground transition hover:border-primary hover:text-foreground"
                  >
                    <Plus className="h-6 w-6" />
                    <span className="text-2xs">上传真人</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* 传多了要能删 —— 管理(删除/配额)在形象库独立页,这里只选不删
                    (选人器不做管理;2026-09-09 老板「我的形象藏得深」)。 */}
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    从你保存过的形象里选一位复用,无需重新上传、无需再次授权。视频形象出片走「视频换口播」:保留原片画面,换新文案对口型。
                  </p>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-primary underline-offset-2 hover:underline"
                    onClick={onGoAvatarLibrary}
                  >
                    去人物库 ▸
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {/* + 格子 = 原「上传真人」tab 的新家(2026-09-09 老板拍板):
                      拍摄要求/授权勾选一样不少,只是收进真要上传的人才展开的子态。 */}
                  <button
                    type="button"
                    aria-label="上传真人"
                    onClick={() => {
                      setAvatarKind("upload");
                      setUploadOpen(true);
                    }}
                    className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed p-1.5 text-muted-foreground transition hover:border-primary hover:text-foreground"
                  >
                    <Plus className="h-6 w-6" />
                    <span className="text-2xs">上传真人</span>
                  </button>
                  {visibleProfiles.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      aria-label={`选择 ${a.label}`}
                      onClick={() => {
                        setAvatarKind("profile");
                        setAvatarProfileId(String(a.id));
                        onPicked();
                      }}
                      className={`group flex flex-col items-center gap-1 rounded-md border p-1.5 transition hover:bg-muted ${
                        avatarProfileId === String(a.id) ? "border-primary ring-2 ring-primary" : ""
                      }`}
                    >
                      <div className="relative w-full">
                        {a.thumbnailUrl ? (
                          a.kind === "video" ? (
                            <video
                              src={`${a.thumbnailUrl}#t=0.1`}
                              muted
                              playsInline
                              preload="metadata"
                              className="aspect-square w-full rounded bg-black object-cover"
                            />
                          ) : (
                            <img
                              src={a.thumbnailUrl}
                              alt={a.label}
                              className="aspect-square w-full rounded object-cover"
                            />
                          )
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center rounded bg-muted">
                            <ImageIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        {a.kind === "video" && (
                          <span className="absolute left-1 top-1 rounded bg-black/70 px-1 py-0.5 text-2xs leading-none text-white">
                            视频
                          </span>
                        )}
                      </div>
                      <span className="text-2xs text-muted-foreground">{a.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                从平台预置主播中选一位出片,无需上传本人照片,也无需授权。
              </p>
              {presetsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {presetAvatars.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      aria-label={`选择 ${a.label}`}
                      onClick={() => {
                        setAvatarKind("preset");
                        setPresetAvatarId(a.id);
                        onPicked();
                      }}
                      className={`group flex flex-col items-center gap-1 rounded-md border p-1.5 transition hover:bg-muted ${
                        presetAvatarId === a.id ? "border-primary ring-2 ring-primary" : ""
                      }`}
                    >
                      <span className="relative w-full">
                        <img
                          src={a.thumbnailUrl}
                          alt={a.label}
                          loading="lazy"
                          className="aspect-square w-full rounded object-cover"
                        />
                        {/*
                         * ★0812 爱民:「这里应该可以有一个查看大图的按钮」。
                         *   格子是 `object-cover` 的方图,人脸会被裁 —— 光看缩略图挑不出人。
                         * ★外层整格已经是 <button>(点=选中),这里只能用 role="button" 的 span:
                         *   button 套 button 是非法 HTML,浏览器会把内层甩出去重排。
                         *   必须 stopPropagation,否则「想看大图」变成「直接选中并关掉抽屉」。
                         */}
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={`看大图 ${a.label}`}
                          data-testid={`dh-preset-zoom-${a.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setPreviewAvatar(a);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setPreviewAvatar(a);
                            }
                          }}
                          className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded bg-black/55 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100 hover:bg-black/75"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </span>
                      </span>
                      <span className="text-2xs text-muted-foreground">{a.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/*
         * 看大图(0812 爱民)。★不用嵌套 Dialog:这个抽屉本身就是 Radix modal,
         *   modal 套 modal 的焦点/滚动锁会互相打架。就在抽屉里盖一层,点任意处关掉。
         * ★只放大,不改选中 —— 看完关掉,选没选还是原样。
         */}
        {previewAvatar && (
          <div
            role="button"
            tabIndex={0}
            aria-label="关闭大图"
            data-testid="dh-preset-preview"
            onClick={() => setPreviewAvatar(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "Enter" || e.key === " ") setPreviewAvatar(null);
            }}
            className="absolute inset-0 z-50 flex cursor-zoom-out flex-col items-center justify-center gap-2 bg-black/80 p-6"
          >
            <img
              src={previewAvatar.thumbnailUrl}
              alt={previewAvatar.label}
              className="max-h-[70%] max-w-full rounded object-contain"
            />
            <span className="text-sm text-white">{previewAvatar.label}</span>
            <span className="text-xs text-white/70">点任意处关闭 · 看大图不会改变你选的形象</span>
          </div>
        )}

        {/* ★显式出口:抽屉是 modal,开着时左边的文案只能看不能点(Radix 给 body 设
            pointer-events:none)。上传 + 勾授权这条路径没有「选中」这个动作,
            没有这个按钮用户就得自己去找 X 或按 Esc 才能回去写文案。 */}
        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button type="button" disabled={!canConfirm} onClick={onPicked}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
